import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Zap, Mail, MessageCircle, ShieldCheck, AlertCircle, ArrowLeft, Loader2, Info,
  Settings2, Send,
} from "lucide-react";

const CHANNEL_META = {
  email: { label: "بريد", Icon: Mail, color: "bg-blue-50 text-blue-800 border-blue-200" },
  whatsapp: { label: "واتساب", Icon: MessageCircle, color: "bg-green-50 text-green-800 border-green-200" },
};

export default function AdminAutomatedFlows() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ purchase_welcome_wa_key: "", purchase_welcome_wa_vars: [], allowed_wa_var_kinds: [] });
  const [waTemplates, setWaTemplates] = useState([]);
  const [testTarget, setTestTarget] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [f, s, w] = await Promise.all([
        api.get("/admin/automated-flows"),
        api.get("/admin/automated-flows/settings"),
        api.get("/whatsapp/templates"),
      ]);
      setFlows(f.data);
      const templates = w.data || [];
      setWaTemplates(templates);
      // Reconcile: if the saved key points to a template that no longer exists,
      // point at the first available template so the dropdown display and React
      // state agree. This prevents "silent save" of a stale/deleted key.
      const saved = s.data || {};
      const currentKey = saved.purchase_welcome_wa_key;
      const exists = currentKey && templates.some((t) => t.key === currentKey);
      if (!exists && templates.length > 0) {
        saved.purchase_welcome_wa_key = templates[0].key;
        toast.warning(
          `القالب السابق «${currentKey}» لم يعد موجودًا. تم اختيار «${templates[0].key}» مبدئيًا — اضغط "حفظ الإعدادات" للتأكيد.`
        );
      }
      setSettings(saved);
    } catch (err) {
      toast.error("فشل تحميل التدفقات: " + (err?.response?.data?.detail || "خطأ"));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    try {
      const key = settings.purchase_welcome_wa_key || (waTemplates[0] && waTemplates[0].key);
      if (!key) {
        toast.error("لا يوجد قالب واتساب متاح لاختياره");
        return;
      }
      await api.put("/admin/automated-flows/settings", {
        purchase_welcome_wa_key: key,
        purchase_welcome_wa_vars: settings.purchase_welcome_wa_vars || ["first_name", "product_name", "token"],
      });
      toast.success("تم حفظ الإعدادات");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحفظ");
    }
  };

  const moveVar = (idx, dir) => {
    const vars = [...(settings.purchase_welcome_wa_vars || [])];
    const j = idx + dir;
    if (j < 0 || j >= vars.length) return;
    [vars[idx], vars[j]] = [vars[j], vars[idx]];
    setSettings({ ...settings, purchase_welcome_wa_vars: vars });
  };
  const changeVarKind = (idx, kind) => {
    const vars = [...(settings.purchase_welcome_wa_vars || [])];
    vars[idx] = kind;
    setSettings({ ...settings, purchase_welcome_wa_vars: vars });
  };

  // Group by trigger event so the UI mirrors the mental model:
  //  "When X happens → these messages fire"
  const grouped = flows.reduce((acc, f) => {
    (acc[f.trigger_label] = acc[f.trigger_label] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-automated-flows">
      <div className="flex items-start gap-3 flex-wrap justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-display font-semibold">التدفقات التلقائية</h1>
          </div>
          <p className="text-foreground/60 mt-2 text-sm max-w-2xl">
            هنا تجد كل الرسائل التي يرسلها النظام تلقائيًا. الرسائل الأساسية (مثل ترحيب ما بعد الشراء) لا تحتاج
            إعداد حملة أو تسلسل — تُرسل مباشرة حال تحقّق الحدث. عطّل قالبًا مؤقتًا لإيقاف تدفقه.
          </p>
        </div>
      </div>

      {/* Configurable settings */}
      <Card className="p-4 space-y-3" data-testid="flow-settings">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-primary" />
          <h2 className="text-base font-semibold">إعدادات القوالب المُشغّلة تلقائيًا</h2>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs">قالب واتساب لترحيب ما بعد الشراء</Label>
            <select
              value={settings.purchase_welcome_wa_key || ""}
              onChange={(e) => setSettings({ ...settings, purchase_welcome_wa_key: e.target.value })}
              className="w-full mt-1 rounded-md border px-2 py-1.5 text-sm bg-white"
              data-testid="setting-wa-welcome-key"
            >
              {waTemplates.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name} ({t.key}) {t.content_sid ? "✓" : "⚠ ContentSid ناقص"}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-foreground/50 mt-1">
              اختر أي قالب من قوالب واتساب لديك ليكون هو الرسالة التي تُرسل تلقائيًا عند إتمام الشراء.
              يجب أن يكون القالب مربوطًا بـ ContentSid صالح في Twilio.
            </p>
          </div>
          <div className="flex items-end">
            <Button onClick={saveSettings} data-testid="btn-save-flow-settings">حفظ الإعدادات</Button>
          </div>
        </div>

        {/* Variable slot mapping — matches how the approved Twilio template numbers
            its placeholders. Wrong order = wrong values in the WhatsApp message
            (e.g. name appearing in the activation link). */}
        <div className="border-t pt-3 mt-1">
          <Label className="text-xs font-semibold">ترتيب متغيرات القالب ({(settings.purchase_welcome_wa_vars || []).length} مواضع)</Label>
          <p className="text-[10px] text-foreground/60 mt-1 mb-2">
            الترتيب هنا يجب أن يطابق ترتيب <code dir="ltr">{"{{1}}, {{2}}, {{3}}"}</code> في قالب Twilio المعتمد
            — بما في ذلك أي متغير في زر URL (المتغيرات تُرقّم بشكل متتابع عبر النص والزر).
          </p>
          <div className="space-y-1.5">
            {(settings.purchase_welcome_wa_vars || []).map((kind, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded p-1.5" data-testid={`wa-var-slot-${idx + 1}`}>
                <span className="text-[11px] font-mono text-primary min-w-[2rem]" dir="ltr">{`{{${idx + 1}}}`}</span>
                <select
                  value={kind}
                  onChange={(e) => changeVarKind(idx, e.target.value)}
                  className="flex-1 rounded border px-2 py-1 text-xs bg-white"
                >
                  {(settings.allowed_wa_var_kinds || ["token", "first_name", "full_name", "product_name", "activation_link"]).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
                <Button size="sm" variant="ghost" onClick={() => moveVar(idx, -1)} disabled={idx === 0} className="h-7 w-7 p-0" data-testid={`wa-var-up-${idx}`}>↑</Button>
                <Button size="sm" variant="ghost" onClick={() => moveVar(idx, +1)} disabled={idx === (settings.purchase_welcome_wa_vars || []).length - 1} className="h-7 w-7 p-0" data-testid={`wa-var-down-${idx}`}>↓</Button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-foreground/50 mt-2">
            <strong>ملاحظة تشخيصية:</strong> إذا لاحظت أن الاسم الأول ظهر في رابط التفعيل بدلاً من الرمز
            (<code dir="ltr">?token=Mohammed</code>)، فذلك يعني أن قالب Twilio المُعتمد يستخدم
            {" "}<code dir="ltr">{"{{1}}"}</code> في زر URL (نفس المتغير المُستخدم للاسم في النص).
            الحل الصحيح: عدّل القالب في Twilio Content Editor ليستخدم متغيرًا جديدًا
            {" "}<code dir="ltr">{"{{3}}"}</code> في زر URL ثم أعِد الاعتماد لدى Meta.
            كحل مؤقت هنا: اسحب <code>token</code> إلى الأعلى (سيصلح الرابط لكن ستظهر قيمة الرمز في نص التحية).
            استخدم زر <strong>«إرسال تجريبي»</strong> بجوار "ترحيب واتساب بعد الشراء" أدناه للتحقق فورًا.
          </p>
        </div>
      </Card>

      {loading && (
        <div className="text-center text-sm text-foreground/50 py-10">
          <Loader2 className="h-5 w-5 animate-spin inline" /> جاري التحميل...
        </div>
      )}

      {!loading && Object.entries(grouped).map(([triggerLabel, items]) => (
        <div key={triggerLabel} className="space-y-2" data-testid={`flow-group-${triggerLabel}`}>
          <div className="flex items-center gap-2 text-sm font-medium text-foreground/70">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>{triggerLabel}</span>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {items.map((f) => <FlowCard key={f.id} f={f} onTest={() => setTestTarget(f)} />)}
          </div>
        </div>
      ))}

      {!loading && flows.length === 0 && (
        <Card className="p-8 text-center text-foreground/50">لا توجد تدفقات مسجلة.</Card>
      )}

      <Card className="p-4 bg-blue-50/60 border-blue-200" data-testid="flows-note">
        <div className="flex items-start gap-2 text-sm text-blue-900">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">كيف أوقف رسالة تلقائية؟</p>
            <ul className="list-disc ps-5 space-y-0.5 text-blue-900/80">
              <li>البريد الإلكتروني: من صفحة <Link to="/admin/emails/templates" className="underline">قوالب البريد</Link> اضغط زر "إيقاف" على القالب.</li>
              <li>الواتساب: من صفحة <Link to="/admin/whatsapp" className="underline">لوحة واتساب</Link> يمكنك إزالة/تعطيل الـ ContentSid.</li>
              <li>تسلسلات السلة المهجورة: من صفحة <Link to="/admin/emails/sequences" className="underline">تسلسلات البريد</Link> أوقف التسلسل النشط.</li>
            </ul>
          </div>
        </div>
      </Card>

      {testTarget && (
        <TestFlowDialog flow={testTarget} onClose={() => setTestTarget(null)} />
      )}
    </div>
  );
}

function FlowCard({ f, onTest }) {
  const meta = CHANNEL_META[f.channel] || CHANNEL_META.email;
  const editHref = f.channel === "email" ? "/admin/emails/templates" : "/admin/whatsapp";
  const effKey = f.effective_template_key || f.template_key;

  return (
    <Card
      className={`p-4 space-y-2 ${f.enabled ? "" : "opacity-70"}`}
      data-testid={`flow-${f.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${meta.color}`}>
            <meta.Icon className="h-3 w-3" /> {meta.label}
          </span>
          {f.mandatory && (
            <Badge variant="default" className="bg-black text-white text-[10px] gap-1" data-testid="flow-badge-mandatory">
              <ShieldCheck className="h-3 w-3" /> أساسي
            </Badge>
          )}
          {!f.enabled && (
            <Badge variant="destructive" className="text-[10px] gap-1" data-testid="flow-badge-disabled">
              <AlertCircle className="h-3 w-3" /> متوقف
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {effKey && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1 text-primary hover:bg-primary/10"
              onClick={onTest}
              data-testid={`flow-test-${f.id}`}
            >
              <Send className="h-3 w-3" /> اختبار
            </Button>
          )}
          <Link
            to={editHref}
            className="text-[11px] text-primary hover:underline shrink-0 flex items-center gap-1"
            data-testid={`flow-edit-${f.id}`}
          >
            تعديل <ArrowLeft className="h-3 w-3 rtl:-scale-x-100" />
          </Link>
        </div>
      </div>
      <p className="text-sm text-foreground/80">{f.description}</p>
      <div className="flex flex-wrap gap-2 text-[10px] text-foreground/50 pt-1 border-t border-foreground/5">
        {effKey && (
          <span className="font-mono bg-foreground/5 px-1.5 py-0.5 rounded" dir="ltr" title="مفتاح القالب الفعلي المستخدم">
            {effKey}
          </span>
        )}
        {f.status_detail && (
          <span className="text-red-700">⚠ {f.status_detail}</span>
        )}
      </div>
    </Card>
  );
}

function TestFlowDialog({ flow, onClose }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.trim()) { toast.error("أدخل رقم/بريد المستلم"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/automated-flows/${flow.id}/test`, { to: to.trim() });
      if (data.ok) toast.success(`تم إرسال الاختبار عبر ${flow.channel === "whatsapp" ? "واتساب" : "البريد"}`);
      else toast.error("فشل الإرسال: " + JSON.stringify(data.result || data));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الإرسال");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="flow-test-dialog">
        <DialogHeader>
          <DialogTitle>اختبار: {flow.description?.slice(0, 60)}...</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>
              {flow.channel === "whatsapp" ? "رقم الواتساب (مع رمز الدولة، مثال: +966501234567)" : "بريد المستلم"}
            </Label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              dir="ltr"
              placeholder={flow.channel === "whatsapp" ? "+966501234567" : "you@example.com"}
              data-testid="flow-test-to"
            />
            <p className="text-[11px] text-foreground/50 mt-1">
              سترسل رسالة تجريبية باستخدام قيم افتراضية للمتغيرات. القالب المستخدم:{" "}
              <code dir="ltr" className="bg-foreground/5 px-1 rounded">{flow.effective_template_key || flow.template_key}</code>
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>إلغاء</Button>
            <Button onClick={send} disabled={busy} data-testid="flow-test-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "إرسال الاختبار"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
